import CopyToClipboardButton from "components/copy-to-clipboard-button/CopyToClipboardButton";
import Addendums from "components/Dashboard/DoctorDashboard/notes-and-forms/components/addendums/Addendums";
import { useAddendums } from "components/Dashboard/DoctorDashboard/notes-and-forms/scenes/note-fill-in/contexts/AddendumsContext";
import mergeRefs from "merge-refs";
import copyElementToClipboard from "notes-and-forms-v2/util/copyElementToClipboard";
import canElementBeRequired, {
  canElementHaveInitials,
} from "notes-and-forms-v2/util/elementTypeUtils";
import { forwardRef, useMemo, useRef } from "react";
import getClasses from "utils/getClasses";
import Initials from "../initials/Initials";
import RequiredAsterisk from "../required-asterisk/RequiredAsterisk";
import styles from "./AtomicElementView.module.scss";
import CheckboxesContent, {
  CheckboxesContentForClipboard,
} from "./checkboxes-content/CheckboxesContent";
import CheckboxesGridContent, {
  CheckboxesGridContentForClipboard,
} from "./checkboxes-grid-content/CheckboxesGridContent";
import DateContent, {
  DateContentForClipboard,
} from "./date-content/DateContent";
import DropdownContent, {
  DropdownContentForClipboard,
} from "./dropdown-content/DropdownContent";
import LinearScaleContent, {
  LinearScaleContentForClipboard,
} from "./linear-scale-content/LinearScaleContent";
import ParagraphContent, {
  ParagraphContentForClipboard,
} from "./paragraph-content/ParagraphContent";
import RadiosContent, {
  RadiosContentForClipboard,
} from "./radios-content/RadiosContent";
import RadiosGridContent, {
  RadiosGridContentForClipboard,
} from "./radios-grid-content/RadiosGridContent";
import ShortAnswerContent, {
  ShortAnswerContentForClipboard,
} from "./short-anwer-content/ShortAnswerContent";
import ShortAnswerGroupContent, {
  ShortAnswerGroupContentForClipboard,
} from "./short-anwer-group-content/ShortAnswerGroupContent";
import SignatureContent from "./signature-content/SignatureContent";
import TextOnlyContent, {
  TextOnlyContentForClipboard,
} from "./text-only-content/TextOnlyContent";

export interface ElementViewProps<
  ElementType extends TemplateElement = TemplateElement,
  DataType extends TemplateDataValue = TemplateDataValue,
> {
  element: ElementType;
  data: TemplateDataValue;
  signatureData?: SignatureData;
  onChange: (value: DataType) => void;
  onSignatureChange: (elementId: string, value: SignatureData) => void;
  isPrintMode?: boolean;
  isDownloadMode?: boolean;
  isLocked?: boolean;
  disabled?: boolean;
  showSuicidalWarningInPhq9?: boolean;
  showCopyToClipboard?: boolean;
  theme?: "session-review" | "default";
  options?: TemplateContentOptions;
  triggerValidation?: (key: string) => void;
  showAsMissing?: boolean;
}

export interface ElementViewForClipboardProps<
  ElementType extends TemplateElement = TemplateElement,
  DataType extends TemplateDataValue = TemplateDataValue,
> {
  element: ElementType;
  data?: DataType;
  signatureData?: SignatureData;
}

const AtomicElementView = forwardRef<
  HTMLDivElement,
  ElementViewProps<AtomicTemplateElement>
>((props, ref) => {
  const {
    element,
    data,
    showCopyToClipboard,
    theme,
    triggerValidation = () => {},
    showAsMissing,
  } = props;

  const wrapperDivRef = useRef<HTMLDivElement>(null);

  const { addendums, canAdd, addendum } = useAddendums();

  const showAddendums = useMemo(() => {
    if (element.type === "text-only" || element.type === "unknown") {
      return false;
    }

    return (
      canAdd ||
      addendum?.[element.id] ||
      addendums?.some((addendum) => addendum.addendum?.[element.id])
    );
  }, [element.id, element.type, addendums, canAdd, addendum]);

  return (
    <div
      ref={mergeRefs(ref, wrapperDivRef)}
      className={getClasses(
        styles.item,
        theme === "session-review" && styles["session-review"],
      )}
      onBlur={({ relatedTarget }) => {
        if (wrapperDivRef.current?.contains(relatedTarget as Node | null)) {
          return;
        }
        triggerValidation(element.id);
      }}
    >
      <div className={styles.header}>
        {element.type !== "text-only" &&
          element.type !== "unknown" &&
          element.type !== "signature" && (
            <>
              <div
                className={getClasses(
                  styles.title,
                  !element.title.length &&
                    !showAsMissing &&
                    styles.hideWhenNotHovered,
                )}
              >
                <div>
                  {element.title}
                  {canElementBeRequired(element) && element.isRequired && (
                    <RequiredAsterisk />
                  )}
                </div>
                {showCopyToClipboard && (
                  <div className={styles.hideWhenNotHovered}>
                    <CopyToClipboardButton
                      onClick={() => copyElementToClipboard(element, data)}
                      size="new"
                    />
                  </div>
                )}
              </div>
              {element.description && (
                <div className={styles.description}>{element.description}</div>
              )}
            </>
          )}
        {showAsMissing && (
          <div className={styles.requiredMessage}>
            This is a required question.
          </div>
        )}
      </div>

      <AtomicElementContent {...props} />

      {canElementHaveInitials(element) && element.requireInitials && (
        <Initials {...props} element={element} />
      )}

      {showAddendums && <Addendums id={element.id} />}
    </div>
  );
});

export function AtomicElementViewForClipboard({
  element,
  data,
}: ElementViewForClipboardProps<AtomicTemplateElement>) {
  return (
    <div>
      {element.type !== "text-only" &&
        element.type !== "unknown" &&
        element.type !== "signature" && <h3>{element.title}</h3>}
      <AtomicElementContentForClipboard element={element} data={data} />
    </div>
  );
}

function AtomicElementContent(props: ElementViewProps<AtomicTemplateElement>) {
  const { element, data } = props;

  switch (element.type) {
    case "short-answer": {
      return <ShortAnswerContent {...props} element={element} />;
    }

    case "short-answer-group": {
      return <ShortAnswerGroupContent {...props} element={element} />;
    }

    case "paragraph": {
      return <ParagraphContent {...props} element={element} />;
    }

    case "radios": {
      return <RadiosContent {...props} element={element} />;
    }

    case "radios-grid": {
      return <RadiosGridContent {...props} element={element} />;
    }

    case "checkboxes-grid": {
      return <CheckboxesGridContent {...props} element={element} />;
    }

    case "linear-scale": {
      return <LinearScaleContent {...props} element={element} />;
    }

    case "checkboxes": {
      return <CheckboxesContent {...props} element={element} />;
    }

    case "text-only": {
      return <TextOnlyContent {...props} element={element} />;
    }

    case "date": {
      return <DateContent {...props} element={element} />;
    }

    case "dropdown": {
      return <DropdownContent {...props} element={element} />;
    }

    case "signature": {
      return <SignatureContent {...props} element={element} />;
    }

    default: {
      return <pre>{JSON.stringify(data, null, 2)}</pre>;
    }
  }
}

function AtomicElementContentForClipboard({
  element,
  data,
}: ElementViewForClipboardProps<AtomicTemplateElement>) {
  switch (element.type) {
    case "short-answer": {
      return <ShortAnswerContentForClipboard element={element} data={data} />;
    }

    case "short-answer-group": {
      return (
        <ShortAnswerGroupContentForClipboard element={element} data={data} />
      );
    }

    case "paragraph": {
      return <ParagraphContentForClipboard element={element} data={data} />;
    }

    case "radios": {
      return <RadiosContentForClipboard element={element} data={data} />;
    }

    case "radios-grid": {
      return <RadiosGridContentForClipboard element={element} data={data} />;
    }

    case "checkboxes": {
      return <CheckboxesContentForClipboard element={element} data={data} />;
    }

    case "checkboxes-grid": {
      return (
        <CheckboxesGridContentForClipboard element={element} data={data} />
      );
    }

    case "linear-scale": {
      return <LinearScaleContentForClipboard element={element} data={data} />;
    }

    case "text-only": {
      return <TextOnlyContentForClipboard element={element} data={data} />;
    }

    case "date": {
      return <DateContentForClipboard element={element} data={data} />;
    }

    case "dropdown": {
      return <DropdownContentForClipboard element={element} data={data} />;
    }

    case "signature": {
      return null;
    }

    default: {
      return (
        <div>
          <div>{element.type} element</div>
          <pre>{JSON.stringify(data, null, 2)}</pre>
        </div>
      );
    }
  }
}

export default AtomicElementView;
